import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { name: "", scope: "Per Customer", min_amount: 0, max_amount: 0, customer_group: "", status: "Active" };

export default function OrderLimits() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/order-limits");
    setRows(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/order-limits", form);
      toast.success("Order limit created");
      setOpen(false);
      setForm(empty);
      load();
    } catch { toast.error("Failed to create"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this order limit?")) return;
    await api.delete(`/order-limits/${id}`);
    load();
  }

  async function toggle(row) {
    const status = row.status === "Active" ? "Draft" : "Active";
    await api.patch(`/order-limits/${row.id}`, { status });
    load();
  }

  return (
    <div data-testid="order-limits-page">
      <PageHeader
        title="Order Limits"
        subtitle="Set minimum and maximum order values per customer or product"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="order-limit-create-btn">
            <Plus size={14} /> Create order limit
          </button>
        }
      />

      <div className="b2b-card">
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No order limits" subtitle="Add limits to enforce minimum and maximum orders." testId="order-limits-empty" />
        ) : (
          <table className="b2b-table" data-testid="order-limits-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Min / Max</th>
                <th>Status</th>
                <th>Created at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`order-limit-row-${r.id}`}>
                  <td className="font-medium">{r.name}</td>
                  <td>{r.scope}</td>
                  <td>${r.min_amount} — ${r.max_amount || "∞"}</td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={r.status === "Active"} onChange={() => toggle(r)} data-testid={`order-limit-toggle-${r.id}`} />
                      <span className="slider" />
                    </label>
                    <span className={`ml-2 badge ${r.status === "Active" ? "badge-active" : "badge-draft"}`}>{r.status}</span>
                  </td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-danger" onClick={() => remove(r.id)} data-testid={`order-limit-delete-${r.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create order limit"
        testId="order-limit-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="order-limit-cancel">Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="order-limit-save">Create limit</button>
          </>
        }
      >
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="order-limit-name" />
          </div>
          <div>
            <label className="form-label">Scope</label>
            <select className="form-select" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="order-limit-scope">
              <option>Per Customer</option>
              <option>Per Product</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Min amount ($)</label>
              <input type="number" className="form-input" value={form.min_amount}
                onChange={(e) => setForm({ ...form, min_amount: Number(e.target.value) })} data-testid="order-limit-min" />
            </div>
            <div>
              <label className="form-label">Max amount ($, 0 for unlimited)</label>
              <input type="number" className="form-input" value={form.max_amount}
                onChange={(e) => setForm({ ...form, max_amount: Number(e.target.value) })} data-testid="order-limit-max" />
            </div>
          </div>
          <div>
            <label className="form-label">Customer group (optional)</label>
            <input className="form-input" value={form.customer_group}
              onChange={(e) => setForm({ ...form, customer_group: e.target.value })} data-testid="order-limit-group" />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="order-limit-status">
              <option>Active</option>
              <option>Draft</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
